import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  StatusCalculatorService,
  ITechStack,
} from '../../shared/services/status-calculator.service';
import { DateEolService } from '../../services/eol/eol.service';
import { ITechStackMatrix } from '../../models';
import { ToolbarComponent } from '../../core/layout/components/toolbar/toolbar.component';
import { FooterComponent } from '../../core/layout/components/footer/footer.component';
import { TitleFormatPipe } from '../../shared/pipes/title-format.pipe';
import { DateFormatPipe } from '../../shared/pipes/date-format.pipe';
import { CommentsService } from '../../services/comments/comments.service';
import { CertificateService } from '../../services/certificates/certificate.service';
import { CommentFormComponent } from './comment-form/comment-form.component';
import {
  CertificateFormComponent,
  CertificateFormData,
} from './certificate-form/certificate-form.component';

export interface ProjectDetail {
  name: string;
  description: string;
  technologies: TechnologyDetail[];
  certificates: CertificateDetail[];
}

export interface TechnologyDetail {
  name: string;
  currentVersion: string;
  endOfLife: string;
  recommendedVersion: string;
  status: 'ACTIVE SUPPORT' | 'SECURITY SUPPORT' | 'NO SUPPORT';
  statusColor: 'green' | 'orange' | 'red';
  icon: string;
}

export interface CertificateDetail {
  name: string;
  environment: string;
  type: 'Front' | 'Back';
  expirationDate: string;
  dev?: string;
  preprod?: string;
  prod?: string;
  status: 'VALID' | 'INVALID';
  statusColor: 'green' | 'red';
  icon: string;
}

export interface ProjectComment {
  comment: string;
  updatedBy: string;
  updatedAt: string;
}

@Component({
  selector: 'app-project-detail',
  imports: [
    CommonModule,
    ToolbarComponent,
    FooterComponent,
    TitleFormatPipe,
    DateFormatPipe,
    CommentFormComponent,
    CertificateFormComponent,
  ],
  templateUrl: './project-detail.component.html',
  styleUrls: ['./project-detail.component.scss'],
})
export class ProjectDetailComponent implements OnInit {
  projectName: string = '';
  projectDetail: ProjectDetail | null = null;
  comments: ProjectComment[] = [];
  showCommentForm = false;
  isSubmittingComment = false;
  showCertificateForm = false;
  isSubmittingCertificate = false;
  eolStack: ITechStackMatrix[] = [];

  // Certificate editing state
  editingCertificate: {
    name: string;
    type: string;
    environment: string;
    newDate: string;
  } | null = null;
  isUpdatingCertificate = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private statusCalculatorService: StatusCalculatorService,
    private commentsService: CommentsService,
    private certificateService: CertificateService,
    private eolService: DateEolService,
  ) {}

  ngOnInit() {
    this.getEOLStack();
    this.route.params.subscribe((params) => {
      console.log(params);
      this.projectName = params['name'] || 'Unknown Project';
      this.loadProjectFromNavigationState();
    });
  }

  loadProjectFromNavigationState() {
    // Try to get data from navigation state
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras.state || history.state;

    console.log(state);

    if (state && state.projectData) {
      this.buildProjectDetailFromData(state.projectData);
    } else {
      // Fallback to hardcoded data if there's no state data
      this.loadProjectDetail();
    }

    // Check if we need to scroll to comments section
    if (state && state.scrollToComments) {
      // Use setTimeout to ensure the view is fully rendered before scrolling
      setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth',
        });
      }, 100);
    }
  }

  buildProjectDetailFromData(projectData: any) {
    const technologies: TechnologyDetail[] = [];

    // Procesar frontend technologies
    if (projectData.frontend && projectData.frontend.stack) {
      projectData.frontend.stack.forEach((tech: any) => {
        const techName = tech.tech || 'Unknown';
        const statusInfo = this.statusCalculatorService.calculateStatus(tech);

        technologies.push({
          name: techName,
          currentVersion: tech.version || 'N/A',
          endOfLife: tech.eol || 'N/A',
          recommendedVersion: tech.latest || 'N/A',
          status: this.mapStatusToLabel(statusInfo),
          statusColor: this.mapStatusToColor(statusInfo),
          icon: this.getIconForTech(techName),
        });
      });
    }

    // Procesar backend technologies
    if (projectData.backend && projectData.backend.stack) {
      projectData.backend.stack.forEach((tech: any) => {
        const techName = tech.tech || 'Unknown';
        const statusInfo = this.statusCalculatorService.calculateStatus(tech);

        technologies.push({
          name: techName,
          currentVersion: tech.version || 'N/A',
          endOfLife: tech.eol || 'N/A',
          recommendedVersion: tech.latest || 'N/A',
          status: this.mapStatusToLabel(statusInfo),
          statusColor: this.mapStatusToColor(statusInfo),
          icon: this.getIconForTech(techName),
        });
      });
    }

    // Procesar comments
    this.comments = this.extractComments(projectData);

    // Create the ProjectDetail object
    this.projectDetail = {
      name: projectData.project || this.projectName,
      description: this.getProjectDescription(projectData),
      technologies: technologies,
      certificates: [], // Will be loaded from service
    };

    // Load certificates from service
    this.loadCertificates();
  }

  mapStatusToLabel(
    status: string,
  ): 'ACTIVE SUPPORT' | 'SECURITY SUPPORT' | 'NO SUPPORT' {
    switch (status) {
      case 'good':
        return 'ACTIVE SUPPORT';
      case 'caution':
        return 'SECURITY SUPPORT';
      case 'danger':
      case 'gray':
      default:
        return 'NO SUPPORT';
    }
  }

  mapStatusToColor(status: string): 'green' | 'orange' | 'red' {
    switch (status) {
      case 'good':
        return 'green';
      case 'caution':
        return 'orange';
      case 'danger':
      case 'gray':
      default:
        return 'red';
    }
  }

  getIconForTech(techName: string): string {
    const techLower = techName.toLowerCase();
    if (techLower.includes('angular')) return 'A';
    if (techLower.includes('react')) return 'R';
    if (techLower.includes('java')) return 'J';
    if (techLower.includes('node')) return 'N';
    if (techLower.includes('spring')) return 'S';
    if (techLower.includes('python')) return 'P';
    return techName.charAt(0).toUpperCase();
  }

  getProjectDescription(projectData: any): string {
    const frontLang = projectData.frontend?.language || '';
    const backLang = projectData.backend?.language || '';

    if (frontLang && backLang) {
      return `Full-stack application using ${frontLang} frontend and ${backLang} backend`;
    } else if (frontLang) {
      return `Frontend application using ${frontLang}`;
    } else if (backLang) {
      return `Backend application using ${backLang}`;
    }

    return 'Project description';
  }

  extractComments(projectData: any): ProjectComment[] {
    if (projectData.comments && Array.isArray(projectData.comments)) {
      return projectData.comments.map((comment: any) => ({
        comment: comment.comment || '',
        updatedBy: comment.updatedBy || 'Unknown',
        updatedAt: comment.updatedAt || new Date().toISOString(),
      }));
    }
    return [];
  }

  /**
   * Loads certificates from the service
   * Delegates data fetching to CertificateService (following SRP and DIP)
   */
  async loadCertificates(): Promise<void> {
    try {
      const certificates = await this.certificateService.fetchCertificates(
        this.projectName,
      );

      if (this.projectDetail) {
        this.projectDetail.certificates = certificates;
      }
    } catch (error) {
      console.error('Error loading certificates:', error);
      // Keep empty array on error
      if (this.projectDetail) {
        this.projectDetail.certificates = [];
      }
    }
  }

  async loadProjectDetail(): Promise<void> {
    // Load certificates from service
    await this.loadCertificates();
  }

  getTechIconPath(statusColor: string): string {
    switch (statusColor) {
      case 'green':
        return 'assets/icons/check.svg';
      case 'orange':
        return 'assets/icons/warning.svg';
      case 'red':
        return 'assets/icons/warning.svg';
      default:
        return 'assets/icons/warning.svg';
    }
  }

  goBack() {
    this.router.navigate(['/guadalajara']);
  }

  toggleCommentForm(): void {
    this.showCommentForm = !this.showCommentForm;
  }

  async handleCommentSubmit(commentText: string): Promise<void> {
    if (!this.projectName || this.isSubmittingComment) {
      return;
    }

    this.isSubmittingComment = true;

    try {
      await this.commentsService.createComment(this.projectName, commentText);

      // Add the new comment to the list
      const newComment: ProjectComment = {
        comment: commentText,
        updatedBy: this.commentsService.prid || 'Unknown',
        updatedAt: new Date().toISOString(),
      };
      this.comments.unshift(newComment);

      this.showCommentForm = false;
      console.log('Comment created successfully');
    } catch (error) {
      console.error('Error creating comment:', error);
    } finally {
      this.isSubmittingComment = false;
    }
  }

  handleCommentCancel(): void {
    this.showCommentForm = false;
  }

  toggleCertificateForm(): void {
    this.showCertificateForm = !this.showCertificateForm;
  }

  async handleCertificateSubmit(formData: CertificateFormData): Promise<void> {
    if (
      !this.projectDetail ||
      this.isSubmittingCertificate ||
      !this.projectName
    ) {
      return;
    }

    this.isSubmittingCertificate = true;

    try {
      // Call service to save certificate to database
      const savedCertificate = await this.certificateService.createCertificate(
        this.projectName,
        formData,
      );

      this.projectDetail.certificates.push(savedCertificate);
      this.showCertificateForm = false;

      console.log('Certificate added successfully:', savedCertificate);
    } catch (error) {
      console.error('Error adding certificate:', error);
      // TODO: Show error message to user
    } finally {
      this.isSubmittingCertificate = false;
    }
  }

  handleCertificateCancel(): void {
    this.showCertificateForm = false;
  }

  /**
   * Checks if a specific date has expired
   * @param dateString - Date string to check
   * @returns true if date is in the past, false otherwise
   */
  isDateExpired(dateString?: string): boolean {
    if (!dateString) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const expirationDate = new Date(dateString);
    expirationDate.setHours(0, 0, 0, 0);

    return expirationDate < today;
  }

  /**
   * Fetches the EOL stack data from the service
   */
  async getEOLStack(): Promise<void> {
    try {
      this.eolStack = await this.eolService.fetchEOLStack();
    } catch (error) {
      console.error('Error fetching EOL stack:', error);
    }
  }

  /**
   * Gets the recommended version for a technology
   * @param techName - Name of the technology
   * @param currentVersion - Current version (optional)
   * @returns Recommended version string
   */
  getRecommendedVersion(techName: string, currentVersion?: string): string {
    return this.eolService._getRecommendedVersion(
      techName,
      this.eolStack,
      currentVersion,
    );
  }

  /**
   * Starts editing a certificate environment
   */
  startEditingCertificate(
    certName: string,
    certType: string,
    environment: 'dev' | 'preprod' | 'prod',
    currentDate: string,
  ): void {
    this.editingCertificate = {
      name: certName,
      type: certType,
      environment,
      newDate: currentDate,
    };
  }

  /**
   * Cancels editing a certificate
   */
  cancelEditingCertificate(): void {
    this.editingCertificate = null;
  }

  /**
   * Checks if a specific certificate environment is being edited
   */
  isEditingEnvironment(
    certName: string,
    certType: string,
    environment: string,
  ): boolean {
    return (
      this.editingCertificate?.name === certName &&
      this.editingCertificate?.type === certType &&
      this.editingCertificate?.environment === environment
    );
  }

  /**
   * Updates the date being edited
   */
  updateEditingDate(newDate: string): void {
    if (this.editingCertificate) {
      this.editingCertificate.newDate = newDate;
    }
  }

  /**
   * Saves the edited certificate date
   */
  async saveCertificateEdit(): Promise<void> {
    if (
      !this.editingCertificate ||
      !this.projectDetail ||
      this.isUpdatingCertificate
    ) {
      return;
    }

    this.isUpdatingCertificate = true;

    try {
      const { name, type, environment, newDate } = this.editingCertificate;

      const updatedCertificate =
        await this.certificateService.updateCertificate(
          this.projectName,
          type as 'Front' | 'Back',
          name,
          environment as 'dev' | 'preprod' | 'prod',
          newDate,
        );

      // Find and update the certificate in the list
      const index = this.projectDetail.certificates.findIndex(
        (cert) => cert.name === name && cert.type === type,
      );

      if (index !== -1) {
        this.projectDetail.certificates[index] = updatedCertificate;
      }

      this.editingCertificate = null;
      console.log('Certificate updated successfully:', updatedCertificate);
    } catch (error) {
      console.error('Error updating certificate:', error);
    } finally {
      this.isUpdatingCertificate = false;
    }
  }
}
